/**
 * منطق العمل الأساسي — مطابق للنسخة الأصلية payroll.html
 * Payroll calculation engine — exact port from the original single-file app.
 */

import * as XLSX from 'xlsx';

export type AttendanceDay = {
  date: string;
  status: string;
  clockIn: string;
  clockOut: string;
  workStartTime: string;
  workEndTime: string;
  workStartMins: number | null;
  workEndMins: number | null;
  ot: number;
  otRaw: number;
  isPresent: boolean;
  isAbsent: boolean;
  lateDed: number;
  earlyDed: number;
  missDed: number;
  missIn: number;
  missOut: number;
  lateDiffMins: number;
  earlyDiffMins: number;
};

export type Thresholds = {
  free_until: number;
  tier1: number;
  tier2: number;
  tier3: number;
};

export type MissDeductions = {
  miss_in: number;
  miss_out: number;
};

export type SalaryRow = {
  ID: string;
  Name: string;
  Job: string;
  'Salary / Day': number;
  'Rate / Hour': number;
};

export type PayrollRow = SalaryRow & {
  'أيام الحضور المستحقة': number;
  'إجمالي ساعات الإضافي': number;
  'أيام جزاء التأخير': number;
  'أيام جزاء الخروج المبكر': number;
  'أيام جزاء النسيان': number;
  'دقائق التأخير': number;
  'دقائق الخروج المبكر': number;
  'تعديلات إدارية (بالأيام)': number;
  'أجر الحضور الأساسي': number;
  'أجر الساعات الإضافية': number;
  'قيمة خصم التأخير': number;
  'قيمة خصم الخروج المبكر': number;
  'قيمة خصم النسيان': number;
  'قيمة التعديلات الإدارية': number;
  'صافي قبل التعديل الإداري': number;
  'صافي القبض المستحق': number;
};

export type ProcessedAttendance = {
  records: Array<{
    ID: string;
    'أيام الحضور المستحقة': number;
    'إجمالي ساعات الإضافي': number;
    'أيام جزاء التأخير': number;
    'أيام جزاء الخروج المبكر': number;
    'أيام جزاء النسيان': number;
    'دقائق التأخير': number;
    'دقائق الخروج المبكر': number;
  }>;
  period: { start: string | null; end: string | null };
  rawById: Record<string, AttendanceDay[]>;
};

/* ───────────── time utilities ───────────── */

export const parseTimeToMinutes = (timeStr: any): number | null => {
  if (timeStr == null || timeStr === '--' || String(timeStr).trim() === '') return null;
  try {
    const parts = String(timeStr).split(':');
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  } catch {
    return null;
  }
};

export const roundToNearestHour = (timeStr: any): number => {
  if (timeStr == null || timeStr === '--' || String(timeStr).trim() === '') return 0;
  try {
    const parts = String(timeStr).split(':');
    const hours = parseInt(parts[0]);
    const minutes = parseInt(parts[1]);
    return minutes >= 30 ? hours + 1 : hours;
  } catch {
    return 0;
  }
};

export const roundOvertimeHours = (val: any): number => {
  let hours = 0;
  if (typeof val === 'number') {
    hours = val;
  } else if (val == null || val === '--' || String(val).trim() === '') {
    return 0;
  } else {
    try {
      const parts = String(val).split(':');
      hours = parseInt(parts[0]) + parseInt(parts[1]) / 60;
    } catch {
      return 0;
    }
  }
  if (hours <= 0) return 0;
  if (hours >= 7.5) return 8;
  const choices = [2, 4, 6, 8];
  let best = choices[0];
  let minDist = Math.abs(choices[0] - hours);
  for (let i = 1; i < choices.length; i++) {
    const d = Math.abs(choices[i] - hours);
    if (d < minDist) {
      best = choices[i];
      minDist = d;
    }
  }
  return best;
};

export const calcLateDeduction = (
  checkinMins: number | null,
  workStartMins: number | null,
  thresholds: Thresholds,
): number => {
  if (checkinMins == null || workStartMins == null) return 0;
  const lateDiff = checkinMins - workStartMins;
  if (lateDiff <= 0) return 0;
  if (lateDiff <= thresholds.free_until) return 0;
  if (lateDiff <= thresholds.tier1) return 0;
  if (lateDiff <= thresholds.tier2) return 0.125;
  if (lateDiff <= thresholds.tier3) return 0.25;
  return 0.5;
};

export const calcEarlyLeaveDeduction = (
  clockOutMins: number | null,
  workEndMins: number | null,
  earlyThresholds: Thresholds,
): number => {
  if (clockOutMins == null || workEndMins == null) return 0;
  const earlyDiff = workEndMins - clockOutMins;
  if (earlyDiff <= 0) return 0;
  if (earlyDiff <= earlyThresholds.free_until) return 0;
  if (earlyDiff <= earlyThresholds.tier1) return 0;
  if (earlyDiff <= earlyThresholds.tier2) return 0.125;
  if (earlyDiff <= earlyThresholds.tier3) return 0.25;
  return 0.5;
};

/* ───────────── date helpers ───────────── */

export const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('ar-EG', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      numberingSystem: 'latn',
    });
  } catch {
    return dateStr;
  }
};

export const formatDateShort = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      numberingSystem: 'latn',
    });
  } catch {
    return dateStr;
  }
};

// ✅ أرقام لاتينية (0-9) مش عربية (٠-٩) عشان تتقرأ بسهولة في كل مكان
export const fmtNum = (n: number): string =>
  Number(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const fmtInt = (n: number): string =>
  Number(n || 0).toLocaleString('en-US');

/* ───────────── main processors ───────────── */

export function processAttendance(
  workbook: XLSX.WorkBook,
  skipRows = 3,
  thresholds: Thresholds,
  earlyThresholds: Thresholds,
  overtimeMode: 'rounded' | 'hourly' = 'rounded',
  missDeductions: MissDeductions = { miss_in: 0.5, miss_out: 0.25 },
  excludeLastDay = false,
  excludeEmployeesWithoutAttendance = true,
): ProcessedAttendance {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
  const dataRows = rows.slice(skipRows);
  if (dataRows.length === 0) throw new Error('ملف البصمة فارغ');

  const headerRow: any[] = dataRows[0] as any[];
  const findIdx = (label: string) => headerRow.findIndex((h: any) => String(h).trim() === label);

  const colIdx = {
    Name: findIdx('Full Name'),
    Date: findIdx('Date'),
    ID: findIdx('ID'),
    Department: findIdx('Department'),
    WorkStartTime: findIdx('Work Start Time'),
    WorkEndTime: findIdx('Work End Time'),
    ClockIn: findIdx('Clock-In Time'),
    ClockOut: findIdx('Clock-Out Time'),
    Status: findIdx('Attendance Status'),
    Late: findIdx('Late Duration'),
    Early: findIdx('Early Leave Duration'),
    OT: findIdx('Overtime Duration'),
  };
  if (colIdx.ID < 0) throw new Error('عمود ID مش موجود');

  const groups: Record<string, AttendanceDay[]> = {};
  let periodStart: string | null = null;
  let periodEnd: string | null = null;

  for (let i = 1; i < dataRows.length; i++) {
    const row: any[] = dataRows[i] as any[];
    const id = String(row[colIdx.ID] || '').trim();
    if (!id) continue;

    const dateStr = colIdx.Date >= 0 ? String(row[colIdx.Date] || '').trim() : '';
    if (dateStr) {
      if (!periodStart || dateStr < periodStart) periodStart = dateStr;
      if (!periodEnd || dateStr > periodEnd) periodEnd = dateStr;
    }

    const workStartTime = colIdx.WorkStartTime >= 0 ? String(row[colIdx.WorkStartTime] || '').trim() : '';
    const workEndTime = colIdx.WorkEndTime >= 0 ? String(row[colIdx.WorkEndTime] || '').trim() : '';
    const workStartMins = parseTimeToMinutes(workStartTime);
    const workEndMins = parseTimeToMinutes(workEndTime);

    const clockIn = colIdx.ClockIn >= 0 ? String(row[colIdx.ClockIn] || '').trim() : '';
    const clockOut = colIdx.ClockOut >= 0 ? String(row[colIdx.ClockOut] || '').trim() : '';
    const status = colIdx.Status >= 0 ? String(row[colIdx.Status] || '').trim() : '';

    const otRaw = roundToNearestHour(row[colIdx.OT]);
    const ot = overtimeMode === 'rounded' ? roundOvertimeHours(otRaw) : otRaw;

    const checkInMins = parseTimeToMinutes(clockIn);
    const checkOutMins = parseTimeToMinutes(clockOut);

    const lateDiffMins =
      checkInMins != null && workStartMins != null ? Math.max(0, checkInMins - workStartMins) : 0;
    const earlyDiffMins =
      checkOutMins != null && workEndMins != null ? Math.max(0, workEndMins - checkOutMins) : 0;

    let lateDed = 0;
    let earlyDed = 0;
    let missDed = 0;
    let missIn = 0;
    let missOut = 0;
    let isAbsent = false;
    let isPresent = true;

    if (status === 'Absent' && clockIn === '--' && clockOut === '--') {
      isAbsent = true;
      isPresent = false;
    } else {
      if (clockIn === '--' || checkInMins == null) {
        missIn = Number(missDeductions?.miss_in ?? 0.5);
        missDed += missIn;
      } else {
        lateDed += calcLateDeduction(checkInMins, workStartMins, thresholds);
      }
      if (clockOut === '--' || checkOutMins == null) {
        missOut = Number(missDeductions?.miss_out ?? 0.25);
        missDed += missOut;
      } else {
        earlyDed += calcEarlyLeaveDeduction(checkOutMins, workEndMins, earlyThresholds);
      }
    }

    if (!groups[id]) groups[id] = [];
    groups[id].push({
      date: dateStr,
      status,
      clockIn,
      clockOut,
      workStartTime,
      workEndTime,
      workStartMins,
      workEndMins,
      ot,
      otRaw,
      isPresent,
      isAbsent,
      lateDed,
      earlyDed,
      missDed,
      missIn,
      missOut,
      lateDiffMins,
      earlyDiffMins,
    });
  }

  // Sort each group by date and apply last-day miss-out exception
  Object.keys(groups).forEach((id) => {
    groups[id].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    if (excludeLastDay && periodEnd) {
      groups[id].forEach((d) => {
        if (d.date === periodEnd && d.missOut > 0) {
          d.missOut = 0;
          d.missDed = d.missIn;
        }
      });
    }
  });

  const records = Object.entries(groups).map(([id, days]) => {
    let pd = 0;
    let ot = 0;
    let l = 0;
    let e = 0;
    let m = 0;
    let totalLateMins = 0;
    let totalEarlyMins = 0;
    for (const d of days) {
      if (d.isAbsent) continue;
      pd += 1;
      ot += d.ot;
      l += d.lateDed;
      e += d.earlyDed;
      m += d.missDed;
      totalLateMins += d.lateDiffMins || 0;
      totalEarlyMins += d.earlyDiffMins || 0;
    }
    return {
      ID: id,
      'أيام الحضور المستحقة': pd,
      'إجمالي ساعات الإضافي': ot,
      'أيام جزاء التأخير': Math.round(l * 1000) / 1000,
      'أيام جزاء الخروج المبكر': Math.round(e * 1000) / 1000,
      'أيام جزاء النسيان': Math.round(m * 1000) / 1000,
      'دقائق التأخير': totalLateMins,
      'دقائق الخروج المبكر': totalEarlyMins,
    };
  });

  // ✅ تجاهل الموظفين اللي مفيش ليهم حضور فعلي في الفترة (أيام الحضور = 0)
  if (excludeEmployeesWithoutAttendance) {
    const keptIds = new Set(
      records.filter((r) => r['أيام الحضور المستحقة'] > 0).map((r) => r.ID),
    );
    Object.keys(groups).forEach((id) => {
      if (!keptIds.has(id)) delete groups[id];
    });
    const filtered = records.filter((r) => keptIds.has(r.ID));
    return { records: filtered, period: { start: periodStart, end: periodEnd }, rawById: groups };
  }

  return { records, period: { start: periodStart, end: periodEnd }, rawById: groups };
}

export function processSalary(workbook: XLSX.WorkBook): SalaryRow[] {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return rows
    .map((r: any) => ({
      ID: String(r['ID'] ?? r['Id'] ?? r['id'] ?? '').trim(),
      Name: String(r['Name'] ?? r['name'] ?? r['Full Name'] ?? '').trim(),
      Job: String(r['Job'] ?? r['job'] ?? r['Position'] ?? '').trim(),
      'Salary / Day': Number(r['Salary / Day'] ?? r['SalaryPerDay'] ?? r['يومية'] ?? 0),
      'Rate / Hour': Number(r['Rate / Hour'] ?? r['RatePerHour'] ?? r['أجر الساعة'] ?? 0),
    }))
    .filter((r) => r.ID);
}

const HOURS_PER_DAY = 8;

export function buildPayroll(
  attRecords: ReturnType<typeof processAttendance>['records'],
  salaryRows: SalaryRow[],
  adminAdj: Record<string, number>,
): PayrollRow[] {
  return attRecords.map((r) => {
    const sal: SalaryRow = salaryRows.find((s) => s.ID === r.ID) || {
      ID: r.ID,
      Name: '(بدون اسم)',
      Job: '—',
      'Salary / Day': 0,
      'Rate / Hour': 0,
    };
    const adjDays = Number(adminAdj?.[r.ID] || 0);
    const salaryPerDay = sal['Salary / Day'] || 0;
    const ratePerHour = sal['Rate / Hour'] || 0;

    const baseSalary = r['أيام الحضور المستحقة'] * salaryPerDay;
    const otPay = r['إجمالي ساعات الإضافي'] * ratePerHour;
    const lateDed = r['أيام جزاء التأخير'] * salaryPerDay;
    const earlyDed = (r['أيام جزاء الخروج المبكر'] || 0) * salaryPerDay;
    const missDed = r['أيام جزاء النسيان'] * salaryPerDay;
    const adminVal = adjDays * salaryPerDay;

    // ✅ صافي قبل التعديل الإداري = بدون الـ adminVal
    const netBeforeAdmin =
      baseSalary + otPay - lateDed - earlyDed - missDed;
    // ✅ صافي بعد التعديل الإداري = مع الـ adminVal (وهو نفسه "صافي القبض المستحق" القديم)
    const net = netBeforeAdmin + adminVal;

    return {
      ...sal,
      ...r,
      'تعديلات إدارية (بالأيام)': adjDays,
      'أجر الحضور الأساسي': baseSalary,
      'أجر الساعات الإضافية': otPay,
      'قيمة خصم التأخير': lateDed,
      'قيمة خصم الخروج المبكر': earlyDed,
      'قيمة خصم النسيان': missDed,
      'قيمة التعديلات الإدارية': adminVal,
      'صافي قبل التعديل الإداري': netBeforeAdmin,
      'صافي القبض المستحق': net,
    } as unknown as PayrollRow;
  });
}

/**
 * ✅ فلتر الـ payrollData — يحذف موظفين بناءً على الإعدادات
 * - بدون حضور: أيام الحضور = 0
 * - مرتبه الإجمالي صفر: صافي القبض ≈ 0 (مع مراعاة التقريب العشري)
 */
export function applyPayrollFilters(
  payroll: PayrollRow[],
  opts: {
    excludeEmployeesWithoutAttendance?: boolean;
    excludeEmployeesWithZeroSalary?: boolean;
  } = {},
): PayrollRow[] {
  return payroll.filter((p) => {
    if (opts.excludeEmployeesWithoutAttendance && p['أيام الحضور المستحقة'] === 0) return false;
    if (opts.excludeEmployeesWithZeroSalary && Math.abs(p['صافي القبض المستحق'] || 0) < 0.01)
      return false;
    return true;
  });
}

/* ───────────── stats ───────────── */

export function calcStats(payroll: PayrollRow[] | null) {
  if (!payroll || !payroll.length) return null;
  return {
    totalNet: payroll.reduce((s, d) => s + d['صافي القبض المستحق'], 0),
    totalNetBeforeAdmin: payroll.reduce((s, d) => s + d['صافي قبل التعديل الإداري'], 0),
    totalAdminAdjValue: payroll.reduce((s, d) => s + d['قيمة التعديلات الإدارية'], 0),
    totalGross: payroll.reduce(
      (s, d) => s + d['أجر الحضور الأساسي'] + d['أجر الساعات الإضافية'] + d['قيمة التعديلات الإدارية'],
      0,
    ),
    totalBase: payroll.reduce((s, d) => s + d['أجر الحضور الأساسي'], 0),
    totalOT: payroll.reduce((s, d) => s + d['أجر الساعات الإضافية'], 0),
    totalLateDed: payroll.reduce((s, d) => s + d['قيمة خصم التأخير'], 0),
    totalEarlyDed: payroll.reduce((s, d) => s + (d['قيمة خصم الخروج المبكر'] || 0), 0),
    totalMissDed: payroll.reduce((s, d) => s + d['قيمة خصم النسيان'], 0),
    totalLateMins: payroll.reduce((s, d) => s + (d['دقائق التأخير'] || 0), 0),
    totalEarlyMins: payroll.reduce((s, d) => s + (d['دقائق الخروج المبكر'] || 0), 0),
    totalAdj: payroll.reduce((s, d) => s + d['قيمة التعديلات الإدارية'], 0),
    employees: payroll.length,
    otHours: payroll.reduce((s, d) => s + d['إجمالي ساعات الإضافي'], 0),
    totalDed: payroll.reduce(
      (s, d) => s + d['قيمة خصم التأخير'] + (d['قيمة خصم الخروج المبكر'] || 0) + d['قيمة خصم النسيان'],
      0,
    ),
    lateDays: payroll.reduce((s, d) => s + d['أيام جزاء التأخير'], 0),
    earlyDays: payroll.reduce((s, d) => s + (d['أيام جزاء الخروج المبكر'] || 0), 0),
    missDays: payroll.reduce((s, d) => s + d['أيام جزاء النسيان'], 0),
    adjDays: payroll.reduce((s, d) => s + d['تعديلات إدارية (بالأيام)'], 0),
    presentDays: payroll.reduce((s, d) => s + d['أيام الحضور المستحقة'], 0),
  };
}

export function groupByJob(payroll: PayrollRow[]) {
  const map: Record<string, {
    count: number;
    days: number;
    ot: number;
    net: number;
    netBeforeAdmin: number;
    ded: number;
    total: number;
  }> = {};
  payroll.forEach((e) => {
    if (!map[e.Job])
      map[e.Job] = { count: 0, days: 0, ot: 0, net: 0, netBeforeAdmin: 0, ded: 0, total: 0 };
    map[e.Job].count += 1;
    map[e.Job].days += e['أيام الحضور المستحقة'];
    map[e.Job].ot += e['إجمالي ساعات الإضافي'];
    map[e.Job].net += e['صافي القبض المستحق'];
    map[e.Job].netBeforeAdmin += e['صافي قبل التعديل الإداري'];
    map[e.Job].ded +=
      e['قيمة خصم التأخير'] + (e['قيمة خصم الخروج المبكر'] || 0) + e['قيمة خصم النسيان'];
    map[e.Job].total +=
      e['أجر الحضور الأساسي'] + e['أجر الساعات الإضافية'] + e['قيمة التعديلات الإدارية'];
  });
  return map;
}
